<template>
  <div page="page">
    <div class="page__title">
     {{ getModuleConfig("my_page.form_title") }}
    </div>
    <div
      class="page__desc"
      v-html="getModuleConfig('my_page.form_desc')"
    ></div>
    <div  class="page__content" v-if="getModuleConfig('my_page.content')" v-html="getModuleConfig('my_page.content')"></div>
    <coupon-filters
      v-if="showFilter"
      class="s-mb-3"
      :options="filterOptions"
      v-model="filter"
    ></coupon-filters>
    <div>
      <Loading v-if="showLoading" loading-message="載入中..." />
      <coupon-card
        v-else-if="!showLoading && currentData.coupons.length > 0"
        v-for="(item, index) in currentData.coupons"
        :key="index"
        :coupon="item"
        :buttonText="couponButtonText(item)"
        :showCode="codeIsVisibleInList"
        @exchange="showCouponModal"
      >
      </coupon-card>
      <coupon-empty v-else />
      <Pagination
        v-model="page"
        :total-page="currentData.meta.lastPage"
        :has-next="currentData.meta.hasNext"
        @input="handlePageIsChanged"
        style="margin: 32px auto 50px"
      />
    </div>
    <coupon-modal
      ref="couponModal"
      :coupon="selectedCoupon"
      @confirmed="refresh"
      :usage-period-title="getModuleConfig('detail.usage_period_title')"
      :show-code="codeIsVisibleInDetail"
      :showQrcodeButton="showQrcodeButton"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import CouponFilters from "@/pages/Dashboard/Coupon/CouponFilters.vue"
import CouponCard from "@/pages/Dashboard/Coupon/CouponCard.vue"
import CouponModal from "@/components/Page/Liff/CouponList/CouponModal.vue"
import couponApi from '@/apis/liff/v2/coupon'
import CouponEmpty from "@/pages/Dashboard/Coupon/CouponEmpty.vue";
import Loading from "@/components/Page/Liff/Shared/Loading";
import Pagination from '@/components/Page/Liff/Shared/PaginationWithoutRedirect'
import Coupon from "@/models/Coupon.model"
import { pushQuery } from "@/utils/UrlMutater";

export default {
  components: { CouponFilters, CouponCard, CouponModal, CouponEmpty, Loading, Pagination },
  inject: ['layoutInstance'],
  data() {
    return {
      showLoading: true,
      provider: null,
      filter: "all",
      page: +this.$route.query.page || 1,
      couponData: {
        all: { coupons: [], meta: { total: 0, lastPage: 1 } },
        expiringSoon: { coupons: [], meta: { total: 0, lastPage: 1 } },
        notYet: { coupons: [], meta: { total: 0, lastPage: 1 } },
      },
      selectedCoupon: {},
    }
  },

  computed: {
    ...mapGetters({ getModuleConfig: 'liffModule/getConfig' }),
    showFilter() {
      return this.provider == "waltily"
    },
    filterOptions() {
      return [
        { key: "all", text: `全部 (${this.couponData.all.meta.total})` },
        { key: "expiringSoon", text: `即將到期 (${this.couponData.expiringSoon.meta.total})` },
        { key: "notYet", text: `尚未開始 (${this.couponData.notYet.meta.total})` },
      ];
    },
    currentData() {
      return this.couponData[this.filter] || { coupons: [], meta: { total: 0, lastPage: 1 } };
    },
    codeIsVisibleInList() {
      return this.getModuleConfig('liff_coupon', 'my.list.redeem_code.enable') ?? true
    },
    codeIsVisibleInDetail() {
      return this.getModuleConfig('liff_coupon', 'my.detail.redeem_code.enable') ?? true
    },
    showQrcodeButton() {
      return this.getModuleConfig('liff_coupon', 'my.detail.qrcode.enable') ?? false
    },
  },
  watch: {
    filter(newVal) {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.fetchCouponsByStatus(newVal, 1);
      }
    },
    'layoutInstance.providerParams': {
      handler(newVal) {
        this.onProviderChanged(newVal);
      },
      deep: true
    }
  },

  mounted() {
    this.fetchAllFirstThenOthersIfWaltily();
  },

  methods: {
    async fetchCouponsByStatus(type, page = 1) {
      this.page = page
      pushQuery({ page })
      this.showLoading = true;
      let status;
      if (type === "all") status = "my";
      else if (type === "expiringSoon") status = "applied_and_expiring_soon";
      else if (type === "notYet") status = "applied_and_not_yet";
      try {
        const apiParams = {
          page,
          status,
          ...this.layoutInstance.providerParams,
        };
        const res = await couponApi.getCouponLists(apiParams);
        this.couponData[type] = {
          coupons: res.data || [],
          meta: {
            total: res.total || 0,
            lastPage: res.lastPage,
            hasNext: res.hasNext,
          }
        };
        if (type === "all") this.provider = res.provider;
      } catch (error) {
        console.log(error);
        this.couponData[type] = {
          coupons: [],
          meta: {
            total: 0,
            lastPage: 1,
          }
        };
        const errorMessage = error.response?.data?.message || "未知錯誤，請聯繫管理員";
        this.$swal.fire({
          type: "error",
          title: "錯誤",
          text: errorMessage
        });
      }
      this.showLoading = false;
    },
    async fetchAllFirstThenOthersIfWaltily() {
      this.showLoading = true;
      await this.fetchCouponsByStatus("all", 1);
      if (this.provider === "waltily") {
        await Promise.all([
          this.fetchCouponsByStatus("expiringSoon", 1),
          this.fetchCouponsByStatus("notYet", 1),
        ]);
      }
      this.showLoading = false;
    },
    showCouponModal(coupon) {
      this.selectedCoupon = coupon
      this.$refs.couponModal.open()
    },
    refresh() {
      this.selectedCoupon = {}
      this.fetchCouponsByStatus(this.filter, 1);
    },
    couponButtonText(coupon) {
      const couponModel = new Coupon(coupon)
      if (!couponModel.isRedeemable) {
        return '查看詳情'
      }

      if (coupon.list_button_text) {
        return coupon.list_button_text
      }

      return coupon.status == 'applied_and_not_yet' ? '尚未開始' : '查看票券'
    },
    onProviderChanged(params) {
      this.provider = params.provider
      this.fetchAllFirstThenOthersIfWaltily()
    },
    handlePageIsChanged(page) {
      this.fetchCouponsByStatus(this.filter, page)
    },
  },
}
</script>

<style lang="scss" scoped></style>
